/**
 * Form Field Components with Width Standards
 * 
 * Enhanced form components that support standardized width tokens.
 * These components wrap the base UI components and add width management.
 */

import * as React from "react";
import { Input } from "./input";
import { Textarea } from "./textarea";
import { Label } from "./label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { FIELD_WIDTHS, type FieldWidth } from "@/constants/field-widths";
import { cn } from "@/lib/utils";

// ============================================================================
// TextField Component
// ============================================================================

export interface TextFieldProps extends Omit<React.ComponentProps<typeof Input>, 'width'> {
  label?: string;
  width?: FieldWidth;
  error?: string;
  hint?: string;
  required?: boolean;
  containerClassName?: string;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, width = 'md', error, hint, required, containerClassName, className, id, ...props }, ref) => {
    const inputId = id || `field-${label?.toLowerCase().replace(/\s+/g, '-')}`;
    
    return (
      <div className={cn("space-y-2", containerClassName)}>
        {label && (
          <Label htmlFor={inputId}>
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </Label>
        )}
        <Input
          ref={ref}
          id={inputId}
          className={cn("field-width-container", className)}
          style={{ maxWidth: FIELD_WIDTHS[width] }}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          {...props}
        />
        {hint && !error && (
          <p id={`${inputId}-hint`} className="text-xs text-muted-foreground">
            {hint}
          </p>
        )}
        {error && (
          <p id={`${inputId}-error`} className="text-sm text-error-500">
            {error}
          </p>
        )}
      </div>
    );
  }
);
TextField.displayName = "TextField";

// ============================================================================
// TextAreaField Component
// ============================================================================

export interface TextAreaFieldProps extends Omit<React.ComponentProps<typeof Textarea>, 'width'> {
  label?: string;
  width?: FieldWidth;
  error?: string;
  hint?: string;
  required?: boolean;
  containerClassName?: string;
}

export const TextAreaField = React.forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  ({ label, width = 'full', error, hint, required, containerClassName, className, id, ...props }, ref) => {
    const textareaId = id || `field-${label?.toLowerCase().replace(/\s+/g, '-')}`;
    
    return (
      <div className={cn("space-y-2", containerClassName)}>
        {label && (
          <Label htmlFor={textareaId}>
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </Label>
        )}
        <Textarea
          ref={ref}
          id={textareaId}
          className={cn("field-width-container", className)}
          style={{ maxWidth: FIELD_WIDTHS[width] }}
          aria-invalid={!!error}
          aria-describedby={error ? `${textareaId}-error` : hint ? `${textareaId}-hint` : undefined}
          {...props}
        />
        {hint && !error && (
          <p id={`${textareaId}-hint`} className="text-xs text-muted-foreground">
            {hint}
          </p>
        )}
        {error && (
          <p id={`${textareaId}-error`} className="text-sm text-error-500">
            {error}
          </p>
        )}
      </div>
    );
  }
);
TextAreaField.displayName = "TextAreaField";

// ============================================================================
// SelectField Component
// ============================================================================

export interface SelectFieldProps {
  label?: string;
  width?: FieldWidth;
  error?: string;
  hint?: string;
  required?: boolean;
  containerClassName?: string;
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  children: React.ReactNode;
  id?: string;
}

export const SelectField = React.forwardRef<HTMLButtonElement, SelectFieldProps>(
  ({ label, width = 'dropdown_sm', error, hint, required, containerClassName, placeholder, children, id, ...props }, ref) => {
    const selectId = id || `field-${label?.toLowerCase().replace(/\s+/g, '-')}`;
    
    return (
      <div className={cn("space-y-2", containerClassName)}>
        {label && (
          <Label htmlFor={selectId}>
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </Label>
        )}
        <Select {...props}>
          <SelectTrigger
            ref={ref}
            id={selectId}
            className="field-width-container"
            style={{ maxWidth: FIELD_WIDTHS[width] }}
            aria-invalid={!!error}
            aria-describedby={error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {children}
          </SelectContent>
        </Select>
        {hint && !error && (
          <p id={`${selectId}-hint`} className="text-xs text-muted-foreground">
            {hint}
          </p>
        )}
        {error && (
          <p id={`${selectId}-error`} className="text-sm text-error-500">
            {error}
          </p>
        )}
      </div>
    );
  }
);
SelectField.displayName = "SelectField";

// ============================================================================
// Utility Components
// ============================================================================

/**
 * FormRow - Layout component for horizontal form field arrangement
 */
export interface FormRowProps {
  children: React.ReactNode;
  className?: string;
  gap?: 'sm' | 'md' | 'lg';
}

export const FormRow: React.FC<FormRowProps> = ({ children, className, gap = 'md' }) => {
  const gapClasses = {
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
  };
  
  return (
    <div className={cn("flex flex-col sm:flex-row sm:items-start", gapClasses[gap], className)}>
      {children}
    </div>
  );
};

/**
 * FormSection - Grouping component for related form fields
 */
export interface FormSectionProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

export const FormSection: React.FC<FormSectionProps> = ({ title, description, children, className }) => {
  return (
    <div className={cn("space-y-4", className)}>
      {(title || description) && (
        <div className="space-y-1">
          {title && <h3 className="text-lg font-medium">{title}</h3>}
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      )}
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
};
